// src/services/tables/keeperRosterColumns.js - COLUMNS FOR KEEPER SELECTION ROSTER TABLE
import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { getStatConfigs, formatStatValue } from '../../utils/statMapping';
import { excludeStaticStatConfigs, STATIC_HITTING_FIELDS, STATIC_PITCHING_FIELDS } from '../../utils/statColumnDedup';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

/**
* Create columns for keeper roster table (selected keepers organized by position slots)
* Uses CANONICAL player structure with roster-lineup tableType
* SIGNATURE: render(player, rowType, item) where:
*   - player = canonical player object (or null if empty slot)
 *   - rowType = 'season' (single row mode)
*   - item = { slotPosition: 'C', slotId: 'C_0', player: {...} }
*/
// Counting-stat cell. Rule 2: real players ALWAYS have stats, and the INNER JOIN now
// guarantees only real (identity-resolved) players reach this grid — so a NULL counting
// stat is an ERROR, not a 0. Render the dash (visibly absent, same as the dynamic-category
// cells) NEVER a manufactured `|| 0` that reads as a real value, and surface the anomaly
// (warn) so the data gap gets caught. Projected view is exempt from the warn (a missing
// projection is a legitimate absence, not an error).
const renderCountingStat = (val, player, statLabel, season, format = (v) => v) => {
  if (val == null) {
    if (season !== 'projected') {
      // eslint-disable-next-line no-console
      console.warn(`Keeper grid: ${statLabel} is null for player ${player?.ids?.mlb} (${player?.info?.full_name || player?.info?.last_name || '?'}) — real players always have stats; rendering "-" (Rule 2 data anomaly).`);
    }
    return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
  }
  return <span className={dynastyTheme.classes.text.white}>{format(val)}</span>;
};

export const createKeeperRosterColumns = ({
  onPlayerClick,
  onRemoveKeeper,
  isLocked = false,
  playerType = 'hitting',
  leagueSettings = null,
  season = 'current',
  marketValues = {},
  projectionsYear = null,
  onSelectExtension = null,
  extensionSelections = {}
}) => {
const isPitcher = playerType === 'pitching';

// Get dynamic stat categories from league scoring_categories
const scoringCategories = leagueSettings?.league_settings?.scoring_categories || {};
const dynamicCategories = isPitcher
  ? scoringCategories.pitching
  : scoringCategories.hitting;

const columns = [
// Position slot with eligibility info
{
      key: 'slotPosition',
title: 'POS',
width: 80,
cellPadding: 'py-2 px-2',
sortable: false,
      render: (player, rowType, item) => {
      if (player?.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: totals row has no slot (item is null)
      return (
      <span className={`${dynastyTheme.classes.text.primary} text-sm font-bold uppercase tracking-wide`}>
      {item?.slotPosition || '?'}
          </span>
            );
      }
},

// Player name
{
key: 'player_name',
title: 'Player',
width: 180,
cellPadding: 'py-2 pl-2 pr-0',
textAlign: 'text-left',
sortable: false,
allowOverflow: true,
render: (player, rowType, item) => {
if (player?.isTotalsRow) { // DD-STAT-CONSOLIDATION C3: native totals row label
  return <span className={`${dynastyTheme.classes.text.primary} font-bold text-sm`}>Totals</span>;
}
if (!player) {
          return (
    <span className={`${dynastyTheme.classes.text.neutralDark} text-sm italic`}>
      Empty
  </span>
);
}

return (
  <div className="inline-flex items-center w-full">
    <PlayerLink
      mlbId={player.ids?.mlb}
      name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()}
      style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    />
    <InjuryBadge mlbId={player.ids?.mlb} />
  </div>
    );
}
},

// Team
{
key: 'team',
title: 'Team',
      width: 60,
cellPadding: 'py-2 px-2',
sortable: false,
render: (player, rowType, item) => {
if (!player) return null;
return (
    <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
        {player.info?.mlb_team || '-'}
          </span>
    );
  }
}
];

// Add BATTER stats: G, AB, H, (dynamic categories)
if (!isPitcher) {
columns.push(
{
        key: 'games',
title: 'G',
        width: 50,
render: (player, rowType, item) => {
if (!player) return null;
const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
        return renderCountingStat(statsSource?.hitting?.games_played, player, 'G', season);
      }
},
  {
        key: 'at_bats',
    title: 'AB',
    width: 50,
  render: (player, rowType, item) => {
    if (!player) return null;
    const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
    return renderCountingStat(statsSource?.hitting?.at_bats, player, 'AB', season);
  }
},
{
        key: 'hits',
title: 'H',
        width: 50,
render: (player, rowType, item) => {
if (!player) return null;
const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
  return renderCountingStat(statsSource?.hitting?.hits, player, 'H', season);
}
}
);

// Add dynamic hitting categories with proper field mapping.
// DD-STAT-CONSOLIDATION sweep: drop any that duplicate the static G/AB/H block above.
const hittingConfigs = excludeStaticStatConfigs(getStatConfigs(dynamicCategories, false), STATIC_HITTING_FIELDS);
hittingConfigs.forEach(config => {
  columns.push({
    key: config.label,
    title: config.label,
    width: 60,
    render: (player, rowType, item) => {
      if (!player) return null;
      
      // Parse field path (e.g., 'hitting.runs' -> ['hitting', 'runs'])
      const fieldParts = config.field.split('.');
      const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
      let val = statsSource;
      
      // Navigate nested path
      for (const part of fieldParts) {
        val = val?.[part];
      }
      
      if (val === null || val === undefined) {
        return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
      }
      
      const formatted = formatStatValue(val, config);
      return <span className={dynastyTheme.classes.text.white}>{formatted}</span>;
    }
  });
});
}

// Add PITCHER stats: G, GS, IP, (dynamic categories)
if (isPitcher) {
columns.push(
{
key: 'games',
title: 'G',
width: 50,
render: (player, rowType, item) => {
if (!player) return null;
const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
return renderCountingStat(statsSource?.pitching?.games_played, player, 'G', season);
}
},
{
key: 'games_started',
  title: 'GS',
    width: 50,
      render: (player, rowType, item) => {
          if (!player) return null;
          const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
          return renderCountingStat(statsSource?.pitching?.games_started, player, 'GS', season);
       }
     },
     {
       key: 'innings_pitched',
       title: 'IP',
       width: 60,
       render: (player, rowType, item) => {
          if (!player) return null;
          const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
        return renderCountingStat(statsSource?.pitching?.innings_pitched, player, 'IP', season, (v) => v.toFixed(1));
      }
  }
);

// Add dynamic pitching categories with proper field mapping.
// DD-STAT-CONSOLIDATION sweep: drop any that duplicate the static G/GS/IP block above.
const pitchingConfigs = excludeStaticStatConfigs(getStatConfigs(dynamicCategories, true), STATIC_PITCHING_FIELDS);
pitchingConfigs.forEach(config => {
  columns.push({
    key: config.label,
    title: config.label,
    width: 60,
    render: (player, rowType, item) => {
      if (!player) return null;
      
      // Parse field path (e.g., 'pitching.wins' -> ['pitching', 'wins'])
      const fieldParts = config.field.split('.');
      const statsSource = player?.isTotalsRow ? player.__stats : (season === 'projected' ? player.projected?.stats : player.stats?.prior_season);
      let val = statsSource;
      
      // Navigate nested path
      for (const part of fieldParts) {
        val = val?.[part];
      }
      
      if (val === null || val === undefined) {
        return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
      }
      
      const formatted = formatStatValue(val, config);
      return <span className={dynastyTheme.classes.text.white}>{formatted}</span>;
    }
  });
});
}

// Add Price columns and contract columns
columns.push(
// Current season Price
{
  key: 'baseline_price',
  title: 'Price',
  width: 80,
  render: (player, rowType, item) => {
    if (!player || player.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: no total for money columns
    const price = season === 'projected'
      ? (player.projected?.keeper_cost || player.league_context?.financial?.baseline_price)
      : player.league_context?.financial?.baseline_price;
    return (
      <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
        ${Math.round(price)}
      </span>
    );
  }
},
// Current Market Value
{
  key: 'market_value',
  title: 'MV',
  width: 75,
  render: (player, rowType, item) => {
    if (!player || player.isTotalsRow || season === 'projected') return null;
    const mv = marketValues[player.ids.mlb];
    if (!mv) return <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>-</span>;

    return (
      <span className={`${(mv.current_market_value > 0 || player.league_context?.financial?.baseline_price > 0) ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight} text-sm font-semibold`}>
        {mv.current_market_value > 0 ? `$${Math.round(mv.current_market_value)}` : (player.league_context?.financial?.baseline_price > 0 ? `$${Math.round(player.league_context?.financial?.baseline_price)}` : 'N/A')}
      </span>
    );
  }
},
// Salary (what they're paying)
{
key: 'salary',
  title: 'Salary',
width: 70,
render: (player, rowType, item) => {
if (!player || player.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: no total for money columns
// Bible Rule 9 + § 4.5 amendment: contract_salary may be null (post-decrement keeper);
// fall through to keeper_proposal.cost (system-proposed retention value).
const salary = extensionSelections?.[player.player_id]?.extension_salary
  ?? player.league_context?.financial?.contract_salary
  ?? player.keeper_proposal?.cost;
return (
  <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
  ${Math.round(salary)}
</span>
);
}
},
// Current contract status - shows contract type with years
  {
    key: 'contract_current',
    title: 'Contract',
    width: 90,
    render: (player, rowType, item) => {
      if (!player || player.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: no total for contract column
      // Bible Rule 9 + § 4.5 amendment: contract_years may be null (post-decrement);
      // fall through to keeper_proposal.years_remaining for system-proposed value.
      const years = player.league_context?.financial?.contract_years
        ?? player.keeper_proposal?.years_remaining;
      const type = player.league_context?.financial?.contract_type;

      if (!years || years <= 0 || type === 'FA') {
        return <span className={dynastyTheme.classes.text.neutralDark}>FA</span>;
      }

      const label = type && type.startsWith('OP') ? `${type} (${years}yr)` : `${years}yr`;

      return (
        <span className={`${dynastyTheme.classes.text.white} text-sm`}>
          {label}
        </span>
      );
    }
  },
  // Next season contract - from projected data
  {
    key: 'contract_projected',
    title: `${projectionsYear || ''} Ctr`,
    width: 90,
    render: (player, rowType, item) => {
      if (!player || player.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: no total for contract column

      // Use projected contract data, overridden by extension selection if present
      const projected = player.projected;
      const currentSelection = extensionSelections?.[player.player_id];

      let projectedType, projectedYears;
      if (currentSelection?.extension_type) {
        projectedType = currentSelection.extension_type;
        const opt = (projected?.extension_options || []).find(o => o.contract_type === currentSelection.extension_type);
        projectedYears = opt?.years || projected?.contract_years;
      } else {
        projectedType = projected?.contract_type;
        projectedYears = projected?.contract_years;
      }

      // If no projected data or contract expired
      if (!projected || !projectedYears || projectedYears <= 0 || !projectedType) {
        return (
          <span className={`${dynastyTheme.classes.text.error} text-sm font-semibold`}>
            FA
          </span>
        );
      }

      const projLabel = projectedType && projectedType.startsWith('OP') ? `${projectedType} (${projectedYears}yr)` : `${projectedYears}yr`;

      return (
        <span className={`${dynastyTheme.classes.text.primary} text-sm font-semibold`}>
          {projLabel}
        </span>
      );
    }
  },
  // Extension Options (interactive dropdown when unlocked, text when locked)
  {
    key: 'extensions',
    title: 'Extension',
    width: 150,
    render: (player, rowType, item) => {
      if (!player || player.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: no extension control on totals row

      const extensionOptions = player.projected?.extension_options || [];
      const currentSelection = extensionSelections?.[player.player_id];

      // If no extension options AND no existing selection, show dash
      if (extensionOptions.length === 0 && !currentSelection?.extension_type) {
        return <span className={dynastyTheme.classes.text.neutralDark}>{'—'}</span>;
      }

      // Show existing extension as static text only if options are empty AND locked
      if (extensionOptions.length === 0 && currentSelection?.extension_type) {
        return (
          <span className={`text-xs ${dynastyTheme.classes.text.success}`}>
            {currentSelection.extension_type}: ${Math.round(currentSelection.extension_salary)}
          </span>
        );
      }

      // Locked or no handler: show text summary
      if (isLocked || !onSelectExtension) {
        if (currentSelection?.extension_type) {
          const opt = extensionOptions.find(o => o.contract_type === currentSelection.extension_type);
          return (
            <span className={`text-xs ${dynastyTheme.classes.text.success}`}>
              {currentSelection.extension_type}: {opt ? `${opt.years}yr @ $${Math.round(opt.cost)}` : `$${Math.round(currentSelection.extension_salary)}`}
            </span>
          );
        }
        return <span className={dynastyTheme.classes.text.neutralDark}>{'—'}</span>;
      }

      // Unlocked: interactive dropdown
      return (
        <select
          value={currentSelection?.extension_type || ''}
          onChange={(e) => {
            e.stopPropagation();
            const selected = extensionOptions.find(o => o.contract_type === e.target.value);
            onSelectExtension(player.player_id, selected || null);
          }}
          onClick={(e) => e.stopPropagation()}
          className={`${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} text-xs rounded px-1 py-0.5 w-full border ${dynastyTheme.classes.border.neutral}`}
        >
          <option value="">No Extension</option>
          {extensionOptions.map(opt => (
            <option key={opt.contract_type} value={opt.contract_type}>
              {opt.contract_type}: {opt.years}yr @ ${Math.round(opt.cost)} (+${opt.cost_increase})
            </option>
          ))}
        </select>
      );
    }
  }
);

  // Add Remove button (only if not locked)
  if (!isLocked) {
    columns.push({
      key: 'actions',
      title: '',
      width: 60,
      cellPadding: 'py-2 px-2',
      sortable: false,
      render: (player, rowType, item) => {
        if (!player || player.isTotalsRow) return null; // DD-STAT-CONSOLIDATION C3: no keeper action on totals row
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveKeeper && onRemoveKeeper(player.player_id);
            }}
            className={`
              p-1.5 rounded
              ${dynastyTheme.classes.bg.error}/20
              ${dynastyTheme.classes.text.error}
              hover:${dynastyTheme.classes.bg.error}/40
              ${dynastyTheme.classes.transitionFast}
            `}
            title="Remove keeper"
          >
            <X className="w-4 h-4" />
          </button>
        );
      }
    });
  }

  return columns;
};
